<template>
  <div class="d-flex flex-column" style="gap: 6px">
    <v-row align="center" justify="space-between">
      <v-col cols="auto">
        <h4>Excel- oder CSV-Datei hochladen*</h4>
      </v-col>
      <v-col class="text-right" cols="auto">
        <v-btn
          color="primary"
          size="small"
          variant="outlined"
          @click="downloadTemplate"
        >
          <v-icon color="primary" start size="small">mdi-download</v-icon>
          {{
            type === "bookables"
              ? "Buchungsobjekte-Vorlage"
              : "Veranstaltungen-Vorlage"
          }}
        </v-btn>
      </v-col>
    </v-row>
    <span class="mt-1">
      Fügen Sie die Buchungsdaten in die Datei ein und halten Sie sich an die
      Struktur der Vorlage.
    </span>

    <!-- TODO: replace this with file-upload-component after vue3 migration -->
    <div
      v-if="!dataFile"
      class="file-input-group"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div
        class="upload-section text-center pa-8 rounded-lg"
        :class="{ 'drag-active': isDragging }"
      >
        <input
          ref="dataInput"
          type="file"
          accept=".csv, .xls, .xlsx"
          class="d-none"
          @change="handleFileUpload"
        />
        <v-icon size="32" class="mb-2">mdi-cloud-upload-outline</v-icon>
        <p class="mb-1 font-weight-medium">
          Excel-/CSV-Datei per Drag & Drop für den Massenimport ziehen
        </p>
        <p class="mb-2">oder</p>
        <v-btn
          class="bg-secondary"
          color="black"
          variant="text"
          @click="$refs.dataInput.click()"
        >
          Datei auswählen
        </v-btn>
        <p class="mt-3 text-caption text-grey">
          Akzeptierte Formate: .csv, .xls, .xlsx | Max. Dateigröße: 5 MB
        </p>
      </div>
    </div>
    <v-card
      v-if="dataFile"
      border
      elevation="1"
      class="uploaded-card pa-5 mt-3"
    >
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="green-darken-1" class="mr-3">mdi-file-excel</v-icon>
          <div>
            <span>{{ dataFile.name }}</span>
            <div class="text-caption text-grey">{{ dataFile.size }}</div>
          </div>
        </div>
        <v-icon @click="removeFile">mdi-delete</v-icon>
      </div>
    </v-card>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import ToastService from "@/services/ToastService";
import { mapActions } from "vuex";

export default {
  name: "ExcelCsvUpload",
  props: {
    type: String,
  },
  data() {
    return {
      dataFile: null,
      isDragging: false,
    };
  },
  methods: {
    ...mapActions({
      addToast: "toasts/add",
    }),
    handleFileUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.addToast(
          ToastService.createToast("fileImport.fileTooLarge", "error")
        );
        this.$refs.dataInput.value = null;
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["csv", "xls", "xlsx"].includes(ext)) {
        this.addToast(
          ToastService.createToast("fileImport.invalidFormat", "error")
        );
        this.$refs.dataInput.value = null;
        return;
      }
      this.dataFile = {
        name: file.name,
        size: this.formatBytes(file.size),
      };
      this.parseSheetData(file);
    },
    parseSheetData(file) {
      const ext = file.name.split(".").pop().toLowerCase();
      const reader = new FileReader();

      if (ext === "csv") {
        reader.onload = (e) => {
          const text = e.target.result;
          const workbook = XLSX.read(text, { type: "string" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const sheetData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
          });
          this.processSheetData(sheetData);
        };
        reader.readAsText(file);
      } else {
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const sheetData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
          });
          this.processSheetData(sheetData);
        };
        reader.readAsArrayBuffer(file);
      }
      reader.onerror = () => {
        this.addToast(
          ToastService.createToast("fileImport.processingError", "error")
        );
      };
    },
    processSheetData(sheetData) {
      const headers = sheetData[0];
      const rows = sheetData.slice(1).map((row) => {
        const rowObj = {};
        headers.forEach((h, i) => {
          let val = row[i];
          const colName = h.trim().toLowerCase();
          if (colName === "startdatum" || colName === "enddatum") {
            val = this.parseExcelDate(val);
          }
          if (colName === "startuhrzeit" || colName === "enduhrzeit") {
            val = this.parseTime(val);
          }
          rowObj[h] = val || "";
        });
        return rowObj;
      });
      this.$emit("file-uploaded", { headers, rows });
    },
    parseExcelDate(value) {
      if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
        return value;
      } else if (typeof value === "number") {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (!parsed) return value;
        return this.formatDateToDDMMYYYY(
          new Date(parsed.y, parsed.m - 1, parsed.d)
        );
      }
      return value;
    },
    formatDateToDDMMYYYY(date) {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    parseTime(value) {
      if (typeof value === "string" && /^\d{2}:\d{2}$/.test(value)) {
        return value;
      } else if (typeof value === "number") {
        const totalMinutes = Math.round(value * 24 * 60);
        const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
        const minutes = String(totalMinutes % 60).padStart(2, "0");
        return `${hours}:${minutes}`;
      }
      return value;
    },
    removeFile() {
      this.dataFile = null;
      this.$nextTick(() => {
        if (this.$refs.dataInput) {
          this.$refs.dataInput.value = null;
        }
      });
      this.$emit("file-removed");
    },
    formatBytes(bytes) {
      const sizes = ["Bytes", "KB", "MB", "GB"];
      if (bytes === 0) return "0 Bytes";
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (
        parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i]
      );
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) this.handleFileUpload({ target: { files: [file] } });
    },
    downloadTemplate() {
      const link = document.createElement("a");
      link.href =
        this.type === "bookables"
          ? process.env.VUE_APP_BOOKABLE_TEMPLATE
          : process.env.VUE_APP_EVENT_TEMPLATE;
      link.setAttribute("download", "bookable_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  },
};
</script>

<style scoped>
.v-theme--light.v-icon {
  color: rgba(0, 0, 0, .54);
}
.upload-section {
  border: 2px dashed #ccc;
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
}
.upload-section.drag-active {
  border-color: #2e7d32;
  background-color: #e8f5e9;
  box-shadow: 0 0 10px rgba(46, 125, 50, 0.2);
}
.uploaded-card {
  border-left: 4px solid #2e7d32;
  transition: box-shadow 0.2s;
  border-radius: 12px !important;
}
.font-weight-medium {
  font-weight: 500;
}
</style>
